import type { Passage } from "./types";

export const passage: Passage = {
  slug: "isaiah-41-10",
  reference: "Isaiah 41:10",
  title: "Isaiah 41:10 meaning: fear not, in its actual setting",
  h1: "Isaiah 41:10, and what it promises",
  description:
    "The most-read verse on the world's largest Bible app, four years out of six. Who it was spoken to, what the promise covers, and what it does not.",
  targets: [
    "isaiah 41:10 meaning",
    "fear not for i am with you verse explained",
    "isaiah 41 10 context",
  ],
  published: "2026-08-27",
  standfirst:
    "It has been the most-read verse on YouVersion four years out of the last six. It was first spoken to a nation about to lose everything.",
  verse: {
    text: "Don't you be afraid, for I am with you. Don't be dismayed, for I am your God. I will strengthen you. Yes, I will help you. Yes, I will uphold you with the right hand of my righteousness.",
    ref: "Isaiah 41:10",
  },
  context: [
    {
      text: "But you, Israel, my servant, Jacob whom I have chosen, the offspring of Abraham my friend, you whom I have taken hold of from the ends of the earth …",
      ref: "Isaiah 41:8–9",
    },
    {
      text: "For I, Yahweh your God, will hold your right hand, saying to you, 'Don't be afraid. I will help you.'",
      ref: "Isaiah 41:13",
    },
  ],
  takeaways: [
    "It is addressed to a people, not to an individual: verses 8 and 9 name Israel, Jacob and the offspring of Abraham before the promise is made.",
    "The four promises are presence, strength, help and being held up — none of them is a promise that the frightening thing will not happen, and historically it did.",
    "The setting is the rise of a conquering empire, which the same chapter describes; the verse is spoken into a real political threat rather than into a mood.",
    "That is what makes it usable rather than sentimental: it is a promise about who is present during the thing, addressed to people whose thing was not cancelled.",
  ],
  body: [
    {
      type: "h2",
      id: "who-is-being-spoken-to",
      text: "Who is being spoken to",
    },
    {
      type: "p",
      text: "The two verses immediately before it name the audience, and they name it three times: Israel my servant, Jacob whom I have chosen, the offspring of Abraham my friend. This is not a general assurance floating free of anyone in particular. It is a nation being addressed by its covenant name at a moment when its continued existence is a genuinely open question.",
    },
    {
      type: "p",
      text: "The chapter around it is about a conqueror coming from the east — armies, nations watching, islands afraid. Whatever else Isaiah 41 is doing, it is doing it inside a real geopolitical situation, and the fear it addresses is the fear of people who can see what is coming and cannot stop it.",
    },
    {
      type: "h2",
      id: "what-the-four-promises-are",
      text: "The four promises, and what each covers",
    },
    {
      type: "ol",
      items: [
        "I am with you. Presence, stated first and without conditions. Not rescue — company.",
        "I am your God. The relationship is asserted before anything is done, which is the ground the other three stand on.",
        "I will strengthen you and help you. Capacity for the thing rather than removal of the thing.",
        "I will uphold you with the right hand of my righteousness. Being held up, which is a picture of someone who would otherwise fall.",
      ],
    },
    {
      type: "p",
      text: "Read as a list, the promises share a shape: every one of them describes what happens to the person during the trouble. None of them describes the trouble going away. That is not a downgrade of the verse — it is the reason it survives contact with a life in which frightening things do in fact happen.",
    },
    {
      type: "h2",
      id: "why-this-verse-and-not-another",
      text: "Why this one, out of thirty-one thousand",
    },
    {
      type: "p",
      text: "YouVersion has published it as the most-read verse of the year four times in six, on an app installed over a billion times. Their own explanation is that it meets the deepest need people bring: the assurance that they are not alone. Their most-searched terms across 2025 were love, anxiety and peace — which is the same finding from a different angle, and it says something plain about what people are actually carrying when they open a Bible on a phone.",
    },
  ],
  misreading: {
    claim:
      "That it is a personal guarantee: if you have enough faith, the outcome you are afraid of will not happen.",
    correction: [
      "The people first addressed by this verse went into exile. Jerusalem fell, the temple was destroyed, and the nation was deported — the book of Isaiah says so at length, and the promise here was not withdrawn when it happened. So whatever the verse means, it cannot mean that the feared event is cancelled, because for its first hearers it was not.",
      "What it says instead is more limited and more durable. Four verbs: I am with you, I will strengthen, I will help, I will uphold. Every one is about company and capacity inside the situation. A verse that promised outcomes would have failed within a generation of being written; this one did not, and that is why it is still the verse people reach for at two in the morning.",
      "The practical difference matters. Read as a guarantee, it sets you up to conclude that your faith was insufficient when the thing happens anyway. Read as it stands, it is the assurance that the thing will not be gone through alone — which is the promise it actually makes, and the only one that could be kept.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "It is short enough to learn in about two minutes and it is at its most useful when it is already in your head, because the moments it is for are not moments anybody goes looking for a page. {How-to-memorise-a-verse-so-it-stays} is the method that survives an ordinary week — ninety seconds of review on day one, day three, day seven and then monthly.",
      links: {
        "How-to-memorise-a-verse-so-it-stays":
          "/blog/how-to-memorize-bible-verses",
      },
    },
    {
      type: "p",
      text: "Read it with verse 13 rather than alone. Two verses later the same promise is restated as a physical image — I will hold your right hand — and the pair together is stronger than either, because the second one tells you what the first one looks like.",
    },
    {
      type: "note",
      text: "If the fear it is being applied to is a nightly one, the passages in the night collection are the neighbours of this verse, and several of them are about lying down specifically.",
    },
  ],
  faq: [
    {
      q: "Who was Isaiah 41:10 written to?",
      a: "Israel, named directly in verses 8 and 9 as Jacob and the offspring of Abraham. The setting is the threat of a conquering power described in the same chapter. It is a national address, which is why it reads steadier than a personal reassurance — the people it was made to were in genuine danger.",
    },
    {
      q: "Does Isaiah 41:10 mean nothing bad will happen?",
      a: "No, and the history of its first hearers rules that reading out — the exile happened. The four promises are presence, strength, help and being upheld, all of which describe what happens during trouble rather than instead of it.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so the passage can be printed whole. The wording differs between translations; the four promises do not.",
    },
    {
      q: "Why is this the most popular verse on Bible apps?",
      a: "YouVersion has named it their verse of the year four times in six years and attributes it to the need for assurance of not being alone. Their most-searched terms in 2025 were love, anxiety and peace, which points the same way: the demand is for comfort at a specific moment rather than for doctrine.",
    },
  ],
  related: [
    "how-to-memorize-bible-verses",
    "how-to-stop-scrolling-at-night",
    "morning-quiet-time-routine",
  ],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-rest",
  ],
  relatedPassages: ["philippians-4-6-7", "psalm-23"],
};
